import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Loader, Shield, Award, Clock, Users, Star, CheckCircle, Sparkles, Package, IndianRupee, Share2, Tag, Search, ChevronLeft, ChevronRight, SlidersHorizontal, X, Upload, CircleDollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { CollectibleWithImages } from '../lib/supabase';
import ShareModal from './ShareModal';

export type CollectibleType = 'coin' | 'note';

interface ShareInfo {
  collectible: CollectibleWithImages;
  url: string;
}

interface Category {
  id: string;
  name: string;
}

interface SortOption {
  label: string;
  value: string;
  direction: 'asc' | 'desc';
}

const ITEMS_PER_PAGE = 12;

const SORT_OPTIONS: SortOption[] = [
  { label: 'Newest First', value: 'created_at', direction: 'desc' },
  { label: 'Oldest First', value: 'created_at', direction: 'asc' },
  { label: 'Price: Low to High', value: 'price', direction: 'asc' },
  { label: 'Price: High to Low', value: 'price', direction: 'desc' },
  { label: 'Name: A to Z', value: 'name', direction: 'asc' },
  { label: 'Name: Z to A', value: 'name', direction: 'desc' },
];

function generateRating(id: string): number {
  const hash = id.slice(0, 4).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const normalized = (hash % 100) / 100;
  return 4.7 + (normalized * 0.3);
}

function getOptimizedImageUrl(url: string): string {
  if (url.includes('unsplash.com')) {
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?auto=format&w=400&q=75`;
  }
  return url;
}

function CollectiblesList() {
  const collectionsRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { successMessage?: string } | null)?.successMessage ?? null
  );
  const [collectibles, setCollectibles] = useState<CollectibleWithImages[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [searchFocused, setSearchFocused] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedSort, setSelectedSort] = useState<SortOption>(SORT_OPTIONS[0]);
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(t);
  }, [successMessage]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchCollectibles = async () => {
      try {
        setLoading(true);
        const searchQuery = searchParams.get('search')?.toLowerCase();
        
        let query = supabase
          .from('collectibles')
          .select(`
            *,
            images:collectible_images(
              id,
              image_url,
              order
            ),
            category:categories(
              id,
              name
            )
          `, { count: 'exact' });

        if (searchQuery) {
          query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
        }

        // Only filter by category if one is selected (not "all")
        if (selectedCategory) {
          query = query.eq('category_id', selectedCategory);
        }

        if (priceRange.min) {
          query = query.gte('price', parseInt(priceRange.min));
        }
        if (priceRange.max) {
          query = query.lte('price', parseInt(priceRange.max));
        }

        query = query.order(selectedSort.value, {
          ascending: selectedSort.direction === 'asc'
        });

        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE - 1;
        query = query.range(start, end);

        const { data, error, count } = await query;

        if (error) throw error;
        setCollectibles(data || []);
        setTotalItems(count || 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load collectibles');
      } finally {
        setLoading(false);
      }
    };

    fetchCollectibles();
  }, [searchParams, selectedCategory, currentPage, selectedSort, priceRange]);

  const handleShare = (collectible: CollectibleWithImages) => {
    const url = `${window.location.origin}/${collectible.slug}`;
    setShareInfo({ collectible, url });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ search: searchQuery });
      setCurrentPage(1);
      setSearchFocused(false);
    }
  };

  const handleCategoryChange = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleSortChange = (sort: SortOption) => {
    setSelectedSort(sort);
    setCurrentPage(1);
  };

  const handlePriceRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPriceRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    collectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setPriceRange({ min: '', max: '' });
    setSelectedSort(SORT_OPTIONS[0]);
    setCurrentPage(1);
    if (searchQuery) {
      setSearchQuery('');
      setSearchParams({});
    }
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <>
      {successMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg animate-fade-in">
          <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          <span className="font-medium">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="ml-2 opacity-70 hover:opacity-100 transition-opacity">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}
      <div className="relative bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 -mx-4 px-4 py-20 mb-12 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1597262975002-c5c3b14bbd62?auto=format&w=2000&q=80')] opacity-5 bg-center bg-cover"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-900/20 to-primary-900/40"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-gold-400/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

        <div className="relative max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="h-4 w-4 text-gold-400" />
            <span className="text-sm text-white/90 font-medium">India's Premier Numismatic Marketplace</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight">
            Discover Rare
            <span className="text-gradient-gold block mt-1">Numismatic Treasures</span>
          </h1>
          <p className="text-lg md:text-xl text-primary-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            Explore authenticated coins and currency notes from India's rich history. Every piece tells a story.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
            <Link
              to="/sell"
              className="group relative inline-flex items-center justify-center gap-2 bg-gradient-to-r from-gold-500 to-gold-600 text-white px-8 py-3.5 rounded-full hover:from-gold-600 hover:to-gold-700 transition-all shadow-lg shadow-gold-500/25 hover:shadow-xl hover:shadow-gold-500/30 w-full sm:w-auto font-medium"
            >
              <CircleDollarSign className="h-5 w-5 group-hover:rotate-12 transition-transform" />
              <span>Sell Your Coins</span>
            </Link>
            <button
              onClick={() => collectionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="group inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-3.5 rounded-full hover:bg-white/20 transition-all w-full sm:w-auto font-medium"
            >
              <Search className="h-5 w-5 group-hover:scale-110 transition-transform" />
              <span>Browse Collection</span>
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 text-center flex-1 max-w-[200px] border border-white/10 hover:bg-white/15 transition-colors">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">1000+</div>
              <div className="text-sm text-primary-200">Verified Collectibles</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 text-center flex-1 max-w-[200px] border border-white/10 hover:bg-white/15 transition-colors">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">500+</div>
              <div className="text-sm text-primary-200">Happy Collectors</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 text-center flex-1 max-w-[200px] border border-white/10 hover:bg-white/15 transition-colors">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">100%</div>
              <div className="text-sm text-primary-200">Authentic Items</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mb-10">
        <div className="relative">
          <form onSubmit={handleSearch} className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-gold-500/20 rounded-2xl blur-xl"></div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              placeholder="Search coins, notes, or collections..."
              className="relative w-full pl-14 pr-5 py-4 rounded-2xl border-2 border-gray-100 focus:border-primary-300 focus:ring-4 focus:ring-primary-100/50 bg-white shadow-soft text-gray-800 placeholder-gray-400 transition-all duration-300"
            />
            <div className="absolute left-5 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-lg bg-primary-50">
              <Search className="h-4 w-4 text-primary-600" />
            </div>
          </form>

          {searchFocused && searchQuery && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-10">
              <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                Suggested Searches
              </div>
              <button
                onClick={() => {
                  setSearchQuery(`${searchQuery} coin`);
                  setSearchParams({ search: `${searchQuery} coin` });
                }}
                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
              >
                {searchQuery} coin
              </button>
              <button
                onClick={() => {
                  setSearchQuery(`${searchQuery} note`);
                  setSearchParams({ search: `${searchQuery} note` });
                }}
                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
              >
                {searchQuery} note
              </button>
              <div className="px-4 py-2 text-xs text-gray-500 border-t">
                Press Enter to search
              </div>
            </div>
          )}
        </div>
      </div>

      <div ref={collectionsRef} className="mb-16">
        <div className="flex flex-col items-center mb-10 text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gold-100 to-gold-50 text-gold-800 rounded-full px-4 py-1.5 text-sm font-medium mb-4 border border-gold-200/50">
            <Sparkles className="h-4 w-4" />
            Handpicked Selection
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
            Featured Collections
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mb-8">
            Discover our carefully curated selection of rare and valuable pieces from India's rich numismatic heritage.
          </p>

          <div className="w-full max-w-5xl mb-6">
            {/* Categories - Horizontal scrollable on mobile */}
            <div className="relative">
              {/* Gradient fade indicators */}
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none md:hidden"></div>
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none md:hidden"></div>

              <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 md:flex md:flex-wrap md:justify-center">
                <div className="flex gap-3 md:flex-wrap md:justify-center min-w-max md:min-w-0">
                  {/* All Collections Button */}
                  <button
                    onClick={() => handleCategoryChange(null)}
                    className={`group relative flex items-center gap-2.5 px-5 py-3 rounded-xl transition-all duration-300 whitespace-nowrap ${
                      selectedCategory === null
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-200/50'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-primary-300 hover:shadow-md'
                    }`}
                  >
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                      selectedCategory === null
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-600'
                    }`}>
                      <Package className="h-4 w-4" />
                    </span>
                    <span className="font-medium">All Collections</span>
                  </button>

                  {/* Category Buttons */}
                  {categories.map((category, index) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`group relative flex items-center gap-2.5 px-5 py-3 rounded-xl transition-all duration-300 whitespace-nowrap ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-200/50'
                          : 'bg-white text-gray-700 border border-gray-200 hover:border-primary-300 hover:shadow-md'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <span className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-white/20 text-white'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-primary-100 group-hover:text-primary-600'
                      }`}>
                        {index % 3 === 0 ? (
                          <CircleDollarSign className="h-4 w-4" />
                        ) : index % 3 === 1 ? (
                          <Award className="h-4 w-4" />
                        ) : (
                          <Star className="h-4 w-4" />
                        )}
                      </span>
                      <span className="font-medium">{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sort and Filters Row */}
            <div className="flex items-center justify-center gap-4 mt-6 flex-wrap">
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5 hover:border-gray-300 transition-colors">
                <span className="text-sm text-gray-500">Sort:</span>
                <select
                  value={`${selectedSort.value}-${selectedSort.direction}`}
                  onChange={(e) => {
                    const [value, direction] = e.target.value.split('-');
                    handleSortChange(SORT_OPTIONS.find(
                      opt => opt.value === value && opt.direction === direction
                    ) || SORT_OPTIONS[0]);
                  }}
                  className="bg-transparent text-sm text-gray-700 font-medium focus:outline-none cursor-pointer"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option
                      key={`${option.value}-${option.direction}`}
                      value={`${option.value}-${option.direction}`}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                  showFilters || (priceRange.min || priceRange.max)
                    ? 'bg-primary-100 text-primary-700 border border-primary-200'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span className="text-sm font-medium">Filters</span>
                {(priceRange.min || priceRange.max) && (
                  <span className="flex items-center justify-center w-5 h-5 bg-primary-600 text-white text-xs rounded-full">
                    1
                  </span>
                )}
              </button>

              {(selectedCategory || priceRange.min || priceRange.max || searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                  Clear all
                </button>
              )}
            </div>

            {/* Expanded Filters Panel */}
            {showFilters && (
              <div className="mt-4 p-5 bg-white rounded-2xl border border-gray-200 shadow-soft animate-fade-up">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4 text-primary-600" />
                    Price Range
                  </h3>
                  <button
                    onClick={() => setPriceRange({ min: '', max: '' })}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 hover:bg-gray-100 px-2 py-1 rounded-lg transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                    Reset
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-sm text-gray-600 mb-1.5">Min Price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      <input
                        type="number"
                        name="min"
                        value={priceRange.min}
                        onChange={handlePriceRangeChange}
                        placeholder="0"
                        className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-gray-800"
                      />
                    </div>
                  </div>
                  <div className="pt-6 text-gray-300">—</div>
                  <div className="flex-1">
                    <label className="block text-sm text-gray-600 mb-1.5">Max Price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      <input
                        type="number"
                        name="max"
                        value={priceRange.max}
                        onChange={handlePriceRangeChange}
                        placeholder="Any"
                        className="w-full pl-7 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-gray-800"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {collectibles.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-100">
            <Award className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No items found</h3>
            <p className="text-gray-600">
              {searchParams.get('search')
                ? 'No items match your search criteria. Try different keywords.'
                : 'Be the first to add a listing in this category!'}
            </p>
            <button
              onClick={clearFilters}
              className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {collectibles.map((item) => (
                <div key={item.id} className="group premium-card overflow-hidden">
                  <Link to={`/${item.slug}`} className="block">
                    <div className="aspect-square relative bg-gray-100 overflow-hidden">
                      {item.images && item.images[0] && (
                        <img
                          src={getOptimizedImageUrl(item.images[0].image_url)}
                          alt={item.name}
                          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
                        {item.category?.name && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-white/95 backdrop-blur-sm text-gray-900 shadow-soft">
                            {item.category.name}
                          </span>
                        )}
                        {(item.status === 'sold' || item.status === 'unavailable') && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/95 backdrop-blur-sm text-white shadow-sm">
                            <Tag className="h-3 w-3" />
                            {item.status === 'sold' ? 'Sold' : 'Unavailable'}
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover:translate-y-0">
                        <div className="flex items-center gap-2">
                          <span className="bg-white/95 backdrop-blur-sm text-gray-900 text-xs font-medium px-3 py-1.5 rounded-lg shadow-soft">
                            View Details
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <div className="p-4 bg-white">
                    <div className="flex items-center justify-between mb-1.5">
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-primary-700 transition-colors">
                        {item.name}
                      </h3>
                      <div className="flex items-center bg-gold-100 px-1.5 py-0.5 rounded">
                        <Star className="h-3 w-3 text-gold-600 fill-gold-500" />
                        <span className="ml-0.5 text-xs font-semibold text-gold-700">
                          {generateRating(item.id).toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mb-3 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div>
                        <span className="text-lg font-bold text-gray-900">₹{item.price.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold text-gold-700 bg-gradient-to-r from-gold-100 to-gold-50 px-2 py-1 rounded-full border border-gold-200/50">
                          Limited
                        </span>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleShare(item);
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-all rounded-lg"
                        >
                          <Share2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-4">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-600 hover:text-primary-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-600 hover:text-primary-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        <div className="group relative overflow-hidden bg-gradient-to-br from-white to-primary-50/30 rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-primary-100/50">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative">
            <div className="mb-4 inline-flex p-3.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg shadow-primary-200/50 text-white">
              <Shield className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              100% Authentic
              <CheckCircle className="h-4 w-4 text-primary-500" />
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Every item is meticulously verified by our expert numismatists
            </p>
          </div>
        </div>

        <div className="group relative overflow-hidden bg-gradient-to-br from-white to-gold-50/30 rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gold-100/50">
          <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative">
            <div className="mb-4 inline-flex p-3.5 bg-gradient-to-br from-gold-500 to-gold-600 rounded-xl shadow-lg shadow-gold-200/50 text-white">
              <Award className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              Best Price
              <IndianRupee className="h-4 w-4 text-gold-500" />
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Competitive prices with certificate of authenticity included
            </p>
          </div>
        </div>

        <div className="group relative overflow-hidden bg-gradient-to-br from-white to-primary-50/30 rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-primary-100/50">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative">
            <div className="mb-4 inline-flex p-3.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg shadow-primary-200/50 text-white">
              <Package className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              Fast Delivery
              <Clock className="h-4 w-4 text-primary-500" />
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Secure packaging with fully insured premium shipping
            </p>
          </div>
        </div>

        <div className="group relative overflow-hidden bg-gradient-to-br from-white to-gold-50/30 rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gold-100/50">
          <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative">
            <div className="mb-4 inline-flex p-3.5 bg-gradient-to-br from-gold-500 to-gold-600 rounded-xl shadow-lg shadow-gold-200/50 text-white">
              <Users className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              Expert Support
              <Star className="h-4 w-4 text-gold-500 fill-gold-400" />
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Dedicated assistance from our numismatic experts
            </p>
          </div>
        </div>
      </div>

      {shareInfo && (
        <ShareModal
          isOpen={true}
          onClose={() => setShareInfo(null)}
          title={shareInfo.collectible.name}
          description={shareInfo.collectible.description}
          imageUrl={shareInfo.collectible.images?.[0]?.image_url}
          price={shareInfo.collectible.price}
          url={shareInfo.url}
        />
      )}
    </>
  );
}

export default CollectiblesList;